/**
 * Gallery Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatAspectRatioFormat,
  formatComponentAlignmentFormat,
  formatCornerRadiusFormat,
  formatFromVariableText,
  formatGapFormat,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseAspectRatioFormat,
  parseGapFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import { BaseAiComponentInput } from '../../registry'
import type {
  ComponentAlignment,
  ResponsiveValue,
  SpacingInputValue,
  SpacingUnit,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import galleryTemplate from './gallery.template.json'

// Extract settings from template (template has a wrapper key)
const GALLERY_DEFAULT_TEMPLATE = (galleryTemplate as any)[
  Object.keys(galleryTemplate as any)[0]
] as IGalleryComponentSettings

export interface IGalleryComponentSettings {
  // Data settings
  'gallery-data-source': string
  'gallery-data-image': string
  'gallery-data-caption': string
  'gallery-data-avatar': string
  'gallery-data-like': string
  'gallery-data-tag': string
  'gallery-data-comp-title': VariableTextValue
  'gallery-data-sort-by'?: string
  'gallery-data-sort-type': string
  'gallery-data-filter'?: any
  'gallery-data-group-by'?: any
  'gallery-data-num-items': any

  // Design settings
  'gallery-design-search-bg-color': string
  'gallery-design-filter-chip-color'?: any
  'gallery-design-filter-bg-color': string
  'gallery-design-add-button-color': any
  'gallery-design-width': ResponsiveValue<string[] | number[]>
  'gallery-design-search-width': ResponsiveValue<string[] | number[]>
  'gallery-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'gallery-design-comp-items-align': ResponsiveValue<any>
  'gallery-design-group-header-align': ResponsiveValue<any>
  'gallery-design-comp-title-align': ResponsiveValue<any>
  'gallery-design-search-align': ResponsiveValue<any>
  'gallery-design-pagination-align': ResponsiveValue<any>
  'gallery-design-layout': ResponsiveValue<string>
  'gallery-design-num-cols': ResponsiveValue<number>
  'gallery-design-num-rows': ResponsiveValue<number>
  'gallery-design-pagination': ResponsiveValue<boolean>
  'gallery-design-image-shape': string
  'gallery-design-avatar-shape': string
  'gallery-design-search-corner': any
  'gallery-design-filter-corner-radius': string
  'gallery-design-add-button-corner-radius': string
  'gallery-design-like-icon'?: any
  'gallery-design-margin': ResponsiveValue<SpacingInputValue>
  'gallery-design-headline-margin': ResponsiveValue<SpacingInputValue>
  'gallery-design-group-header-margin': ResponsiveValue<SpacingInputValue>
  'gallery-design-gap': ResponsiveValue<{ value: number; unit: SpacingUnit }>
  'gallery-design-search-margin': ResponsiveValue<SpacingInputValue>
  'gallery-design-filter-margin': ResponsiveValue<SpacingInputValue>
  'gallery-design-aspect-ratio': ResponsiveValue<string>
  'gallery-design-typography': {
    'gallery-design-caption-typography': ResponsiveValue<TypographyValue>
    'gallery-design-comp-title-typography': ResponsiveValue<TypographyValue>
    'gallery-design-group-header-typography': ResponsiveValue<TypographyValue>
    'gallery-design-tag-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'gallery-action-item-click': BuilderActions
  'gallery-action-add-button-click': BuilderActions

  // Behavior settings
  'gallery-behavior-user-filter'?: boolean
  'gallery-behavior-user-search': boolean
  'gallery-behavior-search-results-info': boolean
  'gallery-behavior-add-button-show': boolean
  'gallery-behavior-add-button-position': string
  'gallery-behavior-add-button-visibility'?: any
}

export interface IGalleryComponent extends IBuilderComponent {
  componentId: 'gallery'
  groupId: 'list'
  settings: IGalleryComponentSettings
}

/**
 * Gallery AI Input
 */
export interface AiGalleryInput extends BaseAiComponentInput {
  dataSource: string // Required: Table ID
  galleryTitle?: string[] // Format: ["text:Gallery"] or ["text:Photos from ", "cr:event"]

  // Data columns
  imageColumn?: string // Column ID for image
  captionColumn?: string // Column ID for caption
  avatarColumn?: string // Column ID for avatar
  likeColumn?: string // Column ID for like
  tagColumn?: string // Column ID for tag

  // Sort & Filter
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: number // Number of items to display

  // Layout
  layout?: string // Gallery layout
  columns?: number | string // Number of columns (e.g., 1, 2, 3 or "1 ta:2 de:3")
  rows?: number | string // Number of rows (e.g., 1, 2, 3 or "1 ta:2 de:3")
  aspectRatio?: string // Aspect ratio: "1:1", "16:9", "4:3"

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  gap?: string // Gap between items: "gap:8px"
  cornerRadius?: string // Image corner radius: "none", "small", "medium", "large" or "extra-small", "extra-large", "full"
  avatarCornerRadius?: string // Avatar corner radius: "none", "small", "medium", "large" or "extra-small", "extra-large", "full"

  // Typography
  typography?: {
    caption?: string // Caption typography
    title?: string // Title typography
    groupHeader?: string // Group header typography
    tag?: string // Tag typography
  }

  // Behavior
  showSearch?: boolean // Show search bar
  showFilter?: boolean // Show filter
  showAddButton?: boolean // Show add button
  showPagination?: boolean // Show pagination
}

export class GalleryComp {
  getTemplate(): IGalleryComponentSettings {
    return GALLERY_DEFAULT_TEMPLATE
  }

  /**
   * format gallery component settings to AI gallery input
   */
  format(settings: IGalleryComponentSettings): AiGalleryInput {
    // @ts-ignore
    const result: AiGalleryInput = {
      type: 'gallery',
      dataSource: settings['gallery-data-source'] || '',
    }

    // Data - Source (required)
    if (settings['gallery-data-source']) {
      result.dataSource = settings['gallery-data-source']
    }

    // Data - Columns
    if (settings['gallery-data-image']) {
      result.imageColumn = settings['gallery-data-image']
    }

    if (settings['gallery-data-caption']) {
      result.captionColumn = settings['gallery-data-caption']
    }

    if (settings['gallery-data-avatar']) {
      result.avatarColumn = settings['gallery-data-avatar']
    }

    if (settings['gallery-data-like']) {
      result.likeColumn = settings['gallery-data-like']
    }

    if (settings['gallery-data-tag']) {
      result.tagColumn = settings['gallery-data-tag']
    }

    // Data - Component Title
    if (settings['gallery-data-comp-title']) {
      result.galleryTitle = formatFromVariableText(settings['gallery-data-comp-title'])
    }

    // Data - Sort
    if (settings['gallery-data-sort-by']) {
      result.sortBy = settings['gallery-data-sort-by']
    }

    if (settings['gallery-data-sort-type']) {
      result.sortType = settings['gallery-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['gallery-data-num-items']) {
      if (typeof settings['gallery-data-num-items'] === 'object' && 'value' in settings['gallery-data-num-items']) {
        result.limit = (settings['gallery-data-num-items'] as any).value
      } else {
        result.limit = settings['gallery-data-num-items'] as number
      }
    }

    // Layout - Layout
    if (settings['gallery-design-layout']) {
      result.layout = formatResponsiveValue(settings['gallery-design-layout'], (val) => String(val))
    }

    // Layout - Columns
    if (settings['gallery-design-num-cols']) {
      const allSame = Object.values(settings['gallery-design-num-cols']).every(
        (v) => v === Object.values(settings['gallery-design-num-cols'])[0]
      )
      if (allSame) {
        result.columns = Object.values(settings['gallery-design-num-cols'])[0]
      } else {
        result.columns = formatResponsiveValue(settings['gallery-design-num-cols'], (val) => String(val))
      }
    }

    // Layout - Rows
    if (settings['gallery-design-num-rows']) {
      const allSame = Object.values(settings['gallery-design-num-rows']).every(
        (v) => v === Object.values(settings['gallery-design-num-rows'])[0]
      )
      if (allSame) {
        result.rows = Object.values(settings['gallery-design-num-rows'])[0]
      } else {
        result.rows = formatResponsiveValue(settings['gallery-design-num-rows'], (val) => String(val))
      }
    }

    // Layout - Aspect Ratio
    if (settings['gallery-design-aspect-ratio']) {
      result.aspectRatio = formatAspectRatioFormat(settings['gallery-design-aspect-ratio'])
    }

    // Design - Width
    if (settings['gallery-design-width']) {
      result.width = formatWidthFormat(settings['gallery-design-width'])
    }

    // Design - Alignment
    if (settings['gallery-design-comp-h-align']) {
      result.alignment =
        formatComponentAlignmentFormat(settings['gallery-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Title Alignment
    if (settings['gallery-design-comp-title-align']) {
      const allSame = Object.values(settings['gallery-design-comp-title-align']).every(
        (v) => v === Object.values(settings['gallery-design-comp-title-align'])[0]
      )
      if (allSame) {
        result.titleAlignment = String(Object.values(settings['gallery-design-comp-title-align'])[0])
      } else {
        result.titleAlignment =
          formatComponentAlignmentFormat(settings['gallery-design-comp-title-align'] as any)?.replace(/^h:/, '') ||
          undefined
      }
    }

    // Design - Margin
    if (settings['gallery-design-margin']) {
      result.margin = formatSpacingFormat(settings['gallery-design-margin'])
    }

    // Design - Gap
    if (settings['gallery-design-gap']) {
      result.gap = formatGapFormat(settings['gallery-design-gap'])
    }

    // Design - Corner Radius
    if (settings['gallery-design-image-shape']) {
      result.cornerRadius = formatCornerRadiusFormat(settings['gallery-design-image-shape'])
    }

    // Design - Avatar Corner Radius
    if (settings['gallery-design-avatar-shape']) {
      result.avatarCornerRadius = formatCornerRadiusFormat(settings['gallery-design-avatar-shape'])
    }

    // Design - Typography
    if (settings['gallery-design-typography']) {
      result.typography = {}
      if (settings['gallery-design-typography']['gallery-design-caption-typography']) {
        result.typography.caption = formatTypographyFormat(
          settings['gallery-design-typography']['gallery-design-caption-typography']
        )
      }
      if (settings['gallery-design-typography']['gallery-design-comp-title-typography']) {
        result.typography.title = formatTypographyFormat(
          settings['gallery-design-typography']['gallery-design-comp-title-typography']
        )
      }
      if (settings['gallery-design-typography']['gallery-design-group-header-typography']) {
        result.typography.groupHeader = formatTypographyFormat(
          settings['gallery-design-typography']['gallery-design-group-header-typography']
        )
      }
      if (settings['gallery-design-typography']['gallery-design-tag-typography']) {
        result.typography.tag = formatTypographyFormat(
          settings['gallery-design-typography']['gallery-design-tag-typography']
        )
      }
    }

    // Behavior - Search
    if (settings['gallery-behavior-user-search'] !== undefined) {
      result.showSearch = settings['gallery-behavior-user-search']
    }

    // Behavior - Filter
    if (settings['gallery-behavior-user-filter'] !== undefined) {
      result.showFilter = settings['gallery-behavior-user-filter']
    }

    // Behavior - Add Button
    if (settings['gallery-behavior-add-button-show'] !== undefined) {
      result.showAddButton = settings['gallery-behavior-add-button-show']
    }

    // Behavior - Pagination
    if (settings['gallery-design-pagination']) {
      const allSame = Object.values(settings['gallery-design-pagination']).every(
        (v) => v === Object.values(settings['gallery-design-pagination'])[0]
      )
      if (allSame) {
        result.showPagination = Object.values(settings['gallery-design-pagination'])[0] as boolean
      } else {
        result.showPagination = formatResponsiveValue(settings['gallery-design-pagination'], (val) =>
          String(val)
        ) as any
      }
    }

    return result
  }

  /**
   * parse AI gallery input to gallery component settings
   */
  parse(input: AiGalleryInput): IGalleryComponentSettings {
    const result = deepMerge(GALLERY_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['gallery-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.imageColumn) {
      result['gallery-data-image'] = input.imageColumn
    }

    if (input.captionColumn) {
      result['gallery-data-caption'] = input.captionColumn
    }

    if (input.avatarColumn) {
      result['gallery-data-avatar'] = input.avatarColumn
    }

    if (input.likeColumn) {
      result['gallery-data-like'] = input.likeColumn
    }

    if (input.tagColumn) {
      result['gallery-data-tag'] = input.tagColumn
    }

    // Data - Component Title
    if (input.galleryTitle) {
      result['gallery-data-comp-title'] = parseToVariableText(input.galleryTitle)
    }

    // Data - Sort
    if (input.sortBy) {
      result['gallery-data-sort-by'] = input.sortBy
    }

    if (input.sortType) {
      result['gallery-data-sort-type'] = input.sortType
    }

    // Data - Limit
    if (input.limit !== undefined) {
      result['gallery-data-num-items'] = {
        enabled: true,
        limit: input.limit,
        value: input.limit,
      }
    }

    // Design - Width
    if (input.width) {
      result['gallery-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['gallery-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.hAlign) {
        result['gallery-design-comp-title-align'] = titleAlignParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['gallery-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Gap (simple responsive value, not full spacing object)
    if (input.gap) {
      result['gallery-design-gap'] = parseGapFormat(input.gap)
    }

    // Design - Layout
    if (input.layout) {
      result['gallery-design-layout'] = parseResponsiveFormat(input.layout, (val) => val)
    }

    // Design - Aspect Ratio
    if (input.aspectRatio) {
      result['gallery-design-aspect-ratio'] = parseAspectRatioFormat(input.aspectRatio)
    }

    // Design - Columns
    if (input.columns !== undefined) {
      if (typeof input.columns === 'string') {
        result['gallery-design-num-cols'] = parseResponsiveFormat(input.columns, (val) => parseInt(val))
      } else {
        result['gallery-design-num-cols'] = parseResponsiveFormat(input.columns.toString(), (val) => parseInt(val))
      }
    }

    // Design - Rows
    if (input.rows !== undefined) {
      if (typeof input.rows === 'string') {
        result['gallery-design-num-rows'] = parseResponsiveFormat(input.rows, (val) => parseInt(val))
      } else {
        result['gallery-design-num-rows'] = parseResponsiveFormat(input.rows.toString(), (val) => parseInt(val))
      }
    }

    // Design - Shape
    if (input.cornerRadius) {
      result['gallery-design-image-shape'] = input.cornerRadius
    }

    if (input.avatarCornerRadius) {
      result['gallery-design-avatar-shape'] = input.avatarCornerRadius
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.caption) {
        const captionTypo = parseTypographyFormat(input.typography.caption)
        result['gallery-design-typography']['gallery-design-caption-typography'] = captionTypo
      }

      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['gallery-design-typography']['gallery-design-comp-title-typography'] = titleTypo
      }

      if (input.typography.groupHeader) {
        const groupHeaderTypo = parseTypographyFormat(input.typography.groupHeader)
        result['gallery-design-typography']['gallery-design-group-header-typography'] = groupHeaderTypo
      }

      if (input.typography.tag) {
        const tagTypo = parseTypographyFormat(input.typography.tag)
        result['gallery-design-typography']['gallery-design-tag-typography'] = tagTypo
      }
    }

    // Behavior
    if (input.showSearch !== undefined) {
      result['gallery-behavior-user-search'] = input.showSearch
    }

    if (input.showFilter !== undefined) {
      result['gallery-behavior-user-filter'] = input.showFilter
    }

    if (input.showAddButton !== undefined) {
      result['gallery-behavior-add-button-show'] = input.showAddButton
    }

    if (input.showPagination !== undefined) {
      result['gallery-design-pagination'] = parseResponsiveFormat(
        input.showPagination.toString(),
        (val) => val === 'true'
      )
    }

    return result
  }
}
